<template>
  <div>
    <!-- 问卷进度 -->
    <Progress
      :total="allQuestion.length"
      :curr="currProgress"
      :showText="showProgressText"
    />
    <!-- 问卷题目 -->
    <div class="list-wrapper">
      <div class="list-wrap">
        <div v-if="currQues">
          <Title :title="currQues.title" :desc="currQues.description" />

          <component
            :is="quesComp(currQues.type, currQues.specType)"
            :key="currQues.code"
            :data="currQues"
            :answer="answers[currentIndex]"
            @onChoose="handleChoose"
            @onChooseCascader="handleChooseCascader"
          />
        </div>
      </div>
    </div>

    <!-- 下一步 -->
    <div
      v-if="!isFinish && !isRadio"
      class="btn btn-next"
      :class="{ disabled: !canGoNext }"
      @click="handleNext"
    >
      <span>下一题</span>
      <img src="../../assets/icon-next.svg" alt="" />
    </div>
    <div
      v-if="isFinish"
      class="btn btn-finish"
      :class="{ disabled: !canGoNext }"
      @click="handleFinish"
    >
      完成
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Popup from '@component/Popup/';
import Loading from '@/basic/components/Loading';
import Cascader from './Cascader.vue';
import CitySelector from './CitySelector.vue';
import RadioWithImg from './RadioWithImg.vue';
import SelectContent from './SelectContent.vue';
import Progress from './Progress.vue';
import PreGuide from './PreGuide.vue';
import Title from './QuesTitle.vue';
import {
  IQuestionnaireData,
  IQuestion,
  IOption,
  IRule,
  IAnswerItem,
  CascaderType,
  IPage,
} from './types';

@Component({
  components: {
    Popup,
    SelectContent,
    Progress,
    Cascader,
    RadioWithImg,
    CitySelector,
    PreGuide,
    Title,
  },
})
export default class QuestionnairePy extends Vue {
  // @Prop({ type: Boolean }) isShow!: boolean;
  @Prop({ type: String, default: '' }) surveyCode!: string;
  @Prop({ default: false }) showProgressText!: boolean;

  questionComps: any = {
    RADIO: SelectContent,
    PIC_RADIO: RadioWithImg,
    CHAINED_SELECTS: Cascader,
  };

  questionnaire: IQuestionnaireData | null = null; // 问卷数据
  questions: IQuestion[] = []; // 用户需要答的题目，动态push
  answers: IAnswerItem[] = []; // 用户已选的答案
  currentIndex = 0;
  allQuestion: IQuestion[] = []; // 返回的所有题目
  isFinish = false;
  sending = false;

  get styleTranslate() {
    return `transform: translateX(-${this.currentIndex * 100}vw) `;
  }
  get canGoNext() {
    return !!this.answers[this.currentIndex];
  }

  get rules() {
    return this.questionnaire!.rules;
  }

  get currProgress() {
    if (!this.isFinish) {
      return this.currentIndex + 1;
    } else {
      return this.allQuestion.length;
    }
  }

  get quesComp() {
    return (type: string, specType: CascaderType) => {
      if (type === 'CHAINED_SELECTS' && specType === CascaderType.area) {
        return CitySelector;
      }
      return this.questionComps[type];
    };
  }
  get currQues() {
    return this.questions[this.currentIndex];
  }

  get getQuesByRule() {
    return this.questionnaire?.rules?.length;
  }

  get isRadio() {
    return (
      this.currQues &&
      (this.currQues.type === 'RADIO' || this.currQues.type === 'PIC_RADIO')
    );
  }

  @Watch('currentIndex')
  checkIsShow(newVal: boolean) {
    this.$emit('updateIndex', newVal);
  }

  mounted() {
    this.init();
  }

  async init() {
    try {
      await window.apis.getAnswerHistory({
        survey_code: this.surveyCode,
        channel: 'LandingPageV2',
      });
    } catch {
      console.log('获取历史记录失败');
    }
    this.getQuestions();
  }

  // 获取问卷
  async getQuestions() {
    try {
      const { data } = await window.apis.getQuestionnaire({
        survey_code: this.surveyCode,
      });
      if (data?.code) {
        this.questionnaire = data;
        this.$emit(
          'updateHeader',
          this.questionnaire?.title,
          this.questionnaire?.description
        );
        if (data?.pages?.[0]?.questions) {
          this.allQuestion = data.pages[0].questions;
        }

        this.handleInitQuestion(data);
      }
    } catch (error) {}
  }

  async handlePost(isAuto = false) {
    if (!isAuto && this.sending) return;

    if (!isAuto) {
      if (this.sending) return;
      this.sending = true;
      Loading.show('正在提交');
    }
    try {
      await window.apis.postQuestionnaire({
        survey_code: this.surveyCode,
        questions: this.answers,
        channel: 'LandingPageV2',
        is_finish: isAuto ? false : true,
        production_id: Number(this.$route.params.id),
      });
    } catch (error) {
    } finally {
      if (!isAuto) {
        this.$emit('refresh');
        Loading.close();
        this.sending = false;
      }
    }
  }

  // 题目初始化，已填过的不需要再填
  handleInitQuestion(data: IQuestionnaireData) {
    let answerStorage: string | IAnswerItem[] | null = localStorage.getItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-answer`
    );
    let questionStorage: string | IQuestion[] | null = localStorage.getItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-question`
    );
    const indexStorage = localStorage.getItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-index`
    );

    answerStorage && (answerStorage = JSON.parse(answerStorage));
    questionStorage && (questionStorage = JSON.parse(questionStorage));

    if (answerStorage?.length && questionStorage?.length && indexStorage) {
      // 答过题
      this.answers = [...answerStorage] as IAnswerItem[];
      this.questions = [...questionStorage] as IQuestion[];
      this.currentIndex = Number(indexStorage);

      if (
        this.answers.length &&
        this.questions.length &&
        this.questions.length === this.answers.length
      ) {
        if (
          this.questions[this.questions.length - 1].type === 'CHAINED_SELECTS'
        ) {
          this.handleGetNextQues();
        } else {
          this.handleGetNextQues(
            this.questions[this.questions.length - 1].code,
            this.answers[this.answers.length - 1].options![0].code
          );
        }
      }
    } else {
      // 未答过题
      if (this.allQuestion.length) {
        // if (this.getQuesByRule) {
        //   this.questions = [this.allQuestion[0]];
        // } else {
        //   this.questions = [...this.allQuestion];
        // }
        this.questions = [this.allQuestion[0]];

        localStorage.setItem(
          `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-index`, // 问卷id-pageId
          `${this.currentIndex}`
        );
      }
    }
  }

  handleNext() {
    if (!this.canGoNext) return;
    this.currentIndex = this.currentIndex + 1;

    localStorage.setItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-index`, // 问卷id-pageId
      `${this.currentIndex}`
    );
  }

  handleFinish() {
    if (!this.canGoNext) return;
    this.handlePost();
  }

  handleChoose(questionData: IQuestion, answerData: IOption) {
    this.answers.splice(this.currentIndex, 1, {
      code: questionData.code,
      content: questionData.title,
      options: [
        {
          code: answerData.code,
          content: answerData.content,
        },
      ],
    });

    this.handleGetNextQues(questionData.code, answerData.code);
    this.handleAutoPost();

    if (!this.isFinish) {
      this.handleNext();
    }
  }
  handleChooseCascader(questionData: IQuestion, answerData: IOption[]) {
    const [parent, child] = answerData;
    const codeList = [parent.code];
    const textList = [parent.content];

    if (!parent.code && !child.code) {
      // 默认选项(placeholder占位选项)，选择这个选项时需把答案和下一题删除
      this.handleUnChoose(this.currentIndex);
      this.handleAutoPost();
      return;
    }

    if (child && child.content) {
      codeList.push(child.code);
      textList.push(child.content);
    }
    this.answers.splice(this.currentIndex, 1, {
      code: questionData.code,
      content: questionData.title,
      codeList,
      textList,
    });
    this.handleGetNextQues();
    this.handleAutoPost();
  }

  handleUnChoose(index: number) {
    this.answers.splice(index, this.answers.length - index);
  }

  // 自动提交
  handleAutoPost() {
    this.handlePost(true);

    // localStorage 缓存已填写的问题答案
    localStorage.setItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-answer`, // 问卷id-pageId
      JSON.stringify(this.answers)
    );
    // localStorage 缓存已填写的问题
    localStorage.setItem(
      `${this.questionnaire?.code}-${this.questionnaire?.pages[0].pageCode}-question`, // 问卷id-pageId
      JSON.stringify(this.questions)
    );
  }

  // 获取下一题
  handleGetNextQues(questionCode?: string, answerCode?: string) {
    if (!this.getQuesByRule) {
      this.handleSetNextQues(
        this.currentIndex + 1 < this.allQuestion.length
          ? [this.allQuestion[this.currentIndex + 1]]
          : []
      );
      return;
    }

    const options = this.rules.filter(
      (item: IRule) => item.sourceCode === questionCode
    );

    if (!options.length) {
      this.isFinish = true;
      return;
    }

    const _nextQues: IQuestion[] = [];

    options.forEach((item: IRule) => {
      const { targetCodes, options } = item;
      if (options?.includes(answerCode!)) {
        targetCodes?.forEach((targetCode: string) => {
          const nextQues = this.allQuestion.filter(
            (question) => question.code === targetCode
          );
          if (nextQues?.length) {
            _nextQues.push(nextQues[0]);
          }
        });
      }
    });

    this.handleSetNextQues(_nextQues);
  }
  handleSetNextQues(nextQues: IQuestion[]) {
    if (!nextQues.length) {
      this.isFinish = true;
      return;
    } else {
      this.questions.splice(
        this.currentIndex + 1,
        this.questions.length - this.currentIndex,
        ...nextQues
      );
    }
  }
}
</script>

<style lang="scss" scoped vars="{ques-theme-bg}">
$theme-color: #382d68;
$theme-active: var(--ques-theme-bg);
.questionaire-wrapper {
  display: flex;
  flex-direction: column;
  height: 92vh;
  padding: 42px 0;
}

.list-wrapper {
  flex: 1;
  margin: 50px 0;
  // overflow-y: hidden;
  overflow-x: hidden;
  overflow-y: scroll;
  // width: 100vw;

  .list-wrap {
    display: flex;
    width: 9999px; // todo
    transition: transform 400ms;
  }
}
.btn {
  width: 686px;
  height: 92px;
  margin: 0 auto;
  font-weight: 600;
  font-size: 28px;
  line-height: 92px;
  text-align: center;

  &.disabled {
    opacity: 0.4;
  }
}
.btn-finish {
  color: #ffffff;
  background-color: $theme-active;
  border-radius: 12px;
}
.btn-next {
  display: flex;
  align-items: center;
  justify-content: center;
  color: $theme-active;

  img {
    position: relative;
    left: -80px;
    width: 16px;
    height: 16px;
    margin-left: 12px;
    filter: drop-shadow($theme-active 80px 0);
  }
}
</style>
